import React from 'react';
import { downloadCSV } from '_utils/csv-export';
import { sanitizeExcelWorkbook } from '_utils/sanitize-spreadsheet-cell';

const TOTAL_COLS = 7;
const MAX_SCORE = 50;
const PASS_THRESHOLD = 40;

const SCORECARD_KPIS = [
    {
        section: 'KPI External Point',
        summaryKey: 'externalCallPoint',
        rows: [
            { label: 'Pre-Advised', kpiKey: 'preAdvisedA', scoreAvailable: 1 },
            { label: 'Pre-Advised', kpiKey: 'preAdvisedB', scoreAvailable: 1 },
            { label: 'Not Pre-Advised', kpiKey: 'nonAdvisedA', scoreAvailable: 1 },
        ],
    },
    {
        section: 'KPI Terminal Reception',
        summaryKey: 'ecac',
        rows: [
            { label: 'Pre-Advised', kpiKey: 'preAdvisedA', scoreAvailable: 2 },
            { label: 'Pre-Advised', kpiKey: 'preAdvisedB', scoreAvailable: 2 },
            { label: 'Not Pre-Advised', kpiKey: 'nonAdvisedA', scoreAvailable: 2 },
            { label: 'Not Pre-Advised', kpiKey: 'nonAdvisedB', scoreAvailable: 2 },
        ],
    },
    {
        section: 'KPI Boardings',
        subsection: 'Outbound Boardings',
        summaryKey: 'boardingSla',
        rows: [
            { label: 'Less than 230 seats', kpiKey: 'lt230SeatsA', scoreAvailable: 2.5 },
            { label: 'Less than 230 seats', kpiKey: 'lt230SeatsB', scoreAvailable: 2.5 },
            { label: 'Greater than or equal to 230 seats', kpiKey: 'gte230SeatsA', scoreAvailable: 2.5 },
            { label: 'Greater than or equal to 230 seats', kpiKey: 'gte230SeatsB', scoreAvailable: 2.5 },
        ],
    },
    {
        section: 'EI/FR Outbound Boardings',
        summaryKey: 'boardingSla',
        rows: [
            { label: 'Less than 230 seats - First Wave', kpiKey: 'firstWave', scoreAvailable: 5 },
        ],
    },
    {
        section: 'KPI Inbounds',
        subsection: 'Inbound',
        summaryKey: 'inboundSla',
        rows: [
            { label: 'Less than 230 seats', kpiKey: 'lt230SeatsA', scoreAvailable: 2 },
            { label: 'Less than 230 seats', kpiKey: 'lt230SeatsB', scoreAvailable: 2 },
            { label: 'Less than 230 seats - all disembarked', kpiKey: 'lt230SeatsC', scoreAvailable: 2 },
            { label: 'Greater than or equal to 230 seats', kpiKey: 'gte230SeatsA', scoreAvailable: 2 },
            { label: 'Greater than or equal to 230 seats', kpiKey: 'gte230SeatsB', scoreAvailable: 2 },
            { label: 'Greater than or equal to 230 seats - all disembarked', kpiKey: 'gte230SeatsC', scoreAvailable: 2 },
        ],
    },
    {
        section: 'KPI Terminal Holding Point 218/407',
        subsection: 'Terminal Holding Point',
        summaryKey: 'terminalReception',
        rows: [
            { label: 'Pre-Advised', kpiKey: 'preAdvisedA', scoreAvailable: 2.5 },
            { label: 'Pre-Advised', kpiKey: 'preAdvisedB', scoreAvailable: 2.5 },
            { label: 'Not Pre-Advised', kpiKey: 'nonAdvisedA', scoreAvailable: 2.5 },
            { label: 'Not Pre-Advised', kpiKey: 'nonAdvisedB', scoreAvailable: 2.5 },
        ],
    },
    {
        section: 'Transfer Passengers',
        subsection: 'Terminal Holding Point',
        summaryKey: 'transferSla',
        rows: [
            { label: 'Pre-Advised – All met and achieved all above KPI\'s or within the min airline connect time', kpiKey: 'preAdvised', scoreAvailable: 2 },
        ],
    },
];

const titleStyle = {
    border: '1px solid #1e3a5f',
    padding: '10px 14px',
    textAlign: 'center',
    backgroundColor: '#1e3a5f',
    color: '#fff',
    fontWeight: 700,
    fontSize: '14px',
};

const columnHeaderStyle = {
    border: '1px solid #6b8eb5',
    padding: '8px 10px',
    textAlign: 'center',
    backgroundColor: '#6b8eb5',
    color: '#fff',
    fontWeight: 600,
    fontSize: '12px',
};

const sectionTitleStyle = {
    border: '1px solid #e5e7eb',
    padding: '8px 10px',
    textAlign: 'left',
    backgroundColor: '#f3f4f6',
    fontWeight: 700,
    fontSize: '13px',
    color: '#1f2937',
};

const labelCellStyle = {
    border: '1px solid #e5e7eb',
    padding: '6px 10px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '12px',
    color: '#1f2937',
};

const valueCellStyle = {
    border: '1px solid #e5e7eb',
    padding: '6px 10px',
    textAlign: 'center',
    fontSize: '12px',
    color: '#1f2937',
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
};

function getKpi(summaries, summaryKey, kpiKey) {
    return summaries?.[summaryKey]?.kpis?.[kpiKey] ?? null;
}

function computeScorecardRows(summaries) {
    let grandTotal = 0;
    let grandEarned = 0;

    const sections = SCORECARD_KPIS.map((group) => {
        let sectionAvailable = 0;
        let sectionEarned = 0;
        const rows = group.rows.map((kpi) => {
            const kpiData = getKpi(summaries, group.summaryKey, kpi.kpiKey);
            const actualScore = kpiData?.score ?? null;
            const targetStr = kpiData?.target ?? null;
            const targetNum = targetStr ? parseFloat(targetStr) : null;
            const mins = kpiData?.minutes ?? null;
            const title = kpiData?.title ?? null;
            const total = kpiData?.total ?? null;
            // No passengers to serve means the SLA cannot be breached, so a
            // successfully-returned KPI with zero pax is treated as a pass.
            const noData = kpiData != null && total === 0;
            const met = noData || (actualScore !== null && targetNum !== null && actualScore >= targetNum);
            const earned = met ? kpi.scoreAvailable : 0;
            sectionAvailable += kpi.scoreAvailable;
            sectionEarned += earned;
            return { ...kpi, actualScore, targetStr, targetNum, mins, title, total, noData, met, earned };
        });
        grandTotal += sectionAvailable;
        grandEarned += sectionEarned;
        return { section: group.section, subsection: group.subsection, rows, sectionAvailable, sectionEarned };
    });

    return { sections, grandTotal, grandEarned };
}

const DublinScorecardTable = ({ summaries, errors }) => {
    const hasAnyData = summaries && Object.values(summaries).some(Boolean);
    if (!hasAnyData) {
        return <p style={{ fontSize: '14px', color: '#6b7280' }}>No data available. Run the report to see results.</p>;
    }

    const { sections, grandTotal, grandEarned } = computeScorecardRows(summaries);
    const passed = grandEarned >= PASS_THRESHOLD;

    const handleExportCsv = () => {
        const rows = [];
        rows.push({ KPIs: 'Service Delivery', Detail: '', 'Score available': '', 'Agreed Target': '', 'Pass %': '', Actual: '', Score: '' });
        sections.forEach((group) => {
            rows.push({ KPIs: group.section, Detail: group.subsection ?? '', 'Score available': group.sectionAvailable, 'Agreed Target': '', 'Pass %': '', Actual: '', Score: '' });
            group.rows.forEach((r) => {
                rows.push({
                    KPIs: r.label,
                    Detail: r.title ?? '',
                    'Score available': r.scoreAvailable,
                    'Agreed Target': r.mins != null ? `${r.mins} minutes` : '—',
                    'Pass %': r.targetStr ?? 'N/A',
                    Actual: r.noData ? 'No pax' : r.actualScore !== null ? `${r.actualScore}%` : 'N/A',
                    Score: r.earned,
                });
            });
        });
        rows.push({ KPIs: 'Total', Detail: '', 'Score available': grandTotal, 'Agreed Target': '', 'Pass %': '', Actual: '', Score: grandEarned });
        rows.push({ KPIs: 'Minimum pass rate', Detail: '', 'Score available': PASS_THRESHOLD, 'Agreed Target': '', 'Pass %': '', Actual: '', Score: '' });
        rows.push({ KPIs: 'Result', Detail: '', 'Score available': '', 'Agreed Target': '', 'Pass %': '', Actual: '', Score: passed ? 'PASS' : 'FAIL' });
        downloadCSV(rows, `dublin-scorecard-${new Date().toISOString().slice(0, 10)}.csv`);
    };

    const handleExportExcel = async () => {
        const Excel = await import('exceljs/dist/exceljs.min.js');
        const workbook = new Excel.Workbook();
        const sheet = workbook.addWorksheet('Dublin Scorecard');

        const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        const applyCell = (cell, { fill, font, align = 'center' } = {}) => {
            cell.border = border;
            cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true };
            if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
            if (font) cell.font = font;
        };

        const COLORS = {
            title: 'FF1E3A5F',
            colHeader: 'FF6B8EB5',
            section: 'FFF3F4F6',
            whiteText: 'FFFFFFFF',
            darkText: 'FF1F2937',
            green: 'FFDCFCE7',
            red: 'FFFEE2E2',
            passGreen: 'FF16A34A',
            failRed: 'FFDC2626',
        };

        sheet.getColumn(1).width = 30;
        sheet.getColumn(2).width = 40;
        sheet.getColumn(3).width = 14;
        sheet.getColumn(4).width = 16;
        sheet.getColumn(5).width = 10;
        sheet.getColumn(6).width = 12;
        sheet.getColumn(7).width = 12;

        const titleRow = sheet.addRow(['Service Delivery', '', '', '', '', '', '']);
        sheet.mergeCells(`A${titleRow.number}:G${titleRow.number}`);
        applyCell(titleRow.getCell(1), { fill: COLORS.title, font: { bold: true, color: { argb: COLORS.whiteText }, size: 13 } });
        titleRow.height = 26;

        const headers = ['KPIs', 'Detail', 'Score available', 'Agreed Target', 'Pass %', 'Actual', 'Score'];
        const headerRow = sheet.addRow(headers);
        for (let c = 1; c <= TOTAL_COLS; c++) {
            applyCell(headerRow.getCell(c), { fill: COLORS.colHeader, font: { bold: true, color: { argb: COLORS.whiteText }, size: 11 } });
        }
        headerRow.height = 24;

        sections.forEach((group) => {
            const secRow = sheet.addRow([group.section, group.subsection ?? '', group.sectionAvailable, '', '', '', '']);
            applyCell(secRow.getCell(1), { fill: COLORS.section, font: { bold: true, color: { argb: COLORS.darkText } }, align: 'left' });
            for (let c = 2; c <= TOTAL_COLS; c++) applyCell(secRow.getCell(c), { fill: COLORS.section });
            secRow.height = 22;

            group.rows.forEach((r) => {
                const actual = r.noData ? 'No pax' : r.actualScore !== null ? `${r.actualScore}%` : 'N/A';
                const target = r.mins != null ? `${r.mins} minutes` : '—';
                const row = sheet.addRow([r.label, r.title ?? '', r.scoreAvailable, target, r.targetStr ?? 'N/A', actual, r.earned]);
                applyCell(row.getCell(1), { font: { bold: true }, align: 'left' });
                applyCell(row.getCell(2), { align: 'left' });
                for (let c = 3; c <= TOTAL_COLS; c++) applyCell(row.getCell(c));
                const scoreFill = r.met ? COLORS.green : COLORS.red;
                applyCell(row.getCell(7), { fill: scoreFill });
                row.height = 28;
            });
        });

        const totalRow = sheet.addRow(['Total', '', grandTotal, '', '', '', grandEarned]);
        applyCell(totalRow.getCell(1), { fill: COLORS.section, font: { bold: true, color: { argb: COLORS.darkText } }, align: 'left' });
        for (let c = 2; c <= TOTAL_COLS; c++) applyCell(totalRow.getCell(c), { fill: COLORS.section, font: { bold: true } });
        totalRow.height = 22;

        const passRow = sheet.addRow(['Minimum pass rate', '', PASS_THRESHOLD, '', '', '', '']);
        applyCell(passRow.getCell(1), { align: 'left' });
        for (let c = 2; c <= TOTAL_COLS; c++) applyCell(passRow.getCell(c));
        passRow.height = 22;

        const resultRow = sheet.addRow(['Result', '', '', '', '', '', passed ? 'PASS' : 'FAIL']);
        applyCell(resultRow.getCell(1), { font: { bold: true }, align: 'left' });
        for (let c = 2; c <= 6; c++) applyCell(resultRow.getCell(c));
        applyCell(resultRow.getCell(7), {
            fill: passed ? COLORS.green : COLORS.red,
            font: { bold: true, color: { argb: passed ? COLORS.passGreen : COLORS.failRed } },
        });
        resultRow.height = 22;

        sanitizeExcelWorkbook(workbook);
        const buf = await workbook.xlsx.writeBuffer();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
        link.download = `dublin-scorecard-${new Date().toISOString().slice(0, 10)}.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const errorKeys = errors ? Object.keys(errors).filter((k) => errors[k]) : [];

    return (
        <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '8px' }}>
                <button
                    type="button"
                    onClick={handleExportCsv}
                    style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                    }}
                >
                    Export CSV
                </button>
                <button
                    type="button"
                    onClick={handleExportExcel}
                    style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        backgroundColor: '#dcfce7',
                        color: '#166534',
                        border: '1px solid #bbf7d0',
                        borderRadius: '6px',
                    }}
                >
                    Export Excel
                </button>
            </div>
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={titleStyle} colSpan={TOTAL_COLS}>
                            Service Delivery
                        </th>
                    </tr>
                    <tr>
                        <th style={columnHeaderStyle}>KPIs</th>
                        <th style={columnHeaderStyle}>Detail</th>
                        <th style={columnHeaderStyle}>Score available</th>
                        <th style={columnHeaderStyle}>Agreed Target</th>
                        <th style={columnHeaderStyle}>Pass %</th>
                        <th style={columnHeaderStyle}>Actual</th>
                        <th style={columnHeaderStyle}>Score</th>
                    </tr>
                </thead>
                <tbody>
                    {errorKeys.map((k) => (
                        <tr key={k}>
                            <td colSpan={TOTAL_COLS} style={{ ...valueCellStyle, color: '#dc2626' }}>
                                {k} data failed: {errors[k]}
                            </td>
                        </tr>
                    ))}
                    {sections.map((group, gi) => (
                        <React.Fragment key={gi}>
                            <tr>
                                <td style={sectionTitleStyle} colSpan={TOTAL_COLS}>
                                    {group.section}
                                    {group.subsection && (
                                        <span style={{ fontWeight: 400, fontSize: '11px', color: '#6b7280', marginLeft: '8px' }}>
                                            — {group.subsection}
                                        </span>
                                    )}
                                    <span style={{ float: 'right', fontWeight: 400, fontSize: '12px', color: '#6b7280' }}>
                                        {group.sectionEarned} / {group.sectionAvailable}
                                    </span>
                                </td>
                            </tr>
                            {group.rows.map((row, ri) => {
                                const scoreColor = row.actualScore === null ? '#6b7280' : row.met ? '#16a34a' : '#dc2626';
                                const scoreBg = row.actualScore === null ? undefined : row.met ? '#dcfce7' : '#fee2e2';
                                const actualDisplay = row.noData
                                    ? 'No pax'
                                    : row.actualScore !== null ? `${row.actualScore}%` : 'N/A';
                                return (
                                    <tr key={`${gi}-${ri}`}>
                                        <td style={labelCellStyle}>{row.label}</td>
                                        <td style={{ ...valueCellStyle, textAlign: 'left', fontSize: '11px', color: '#6b7280' }}>
                                            {row.title ?? ''}
                                        </td>
                                        <td style={valueCellStyle}>{row.scoreAvailable}</td>
                                        <td style={valueCellStyle}>
                                            {row.mins != null ? `${row.mins} minutes` : '—'}
                                        </td>
                                        <td style={valueCellStyle}>{row.targetStr ?? 'N/A'}</td>
                                        <td style={valueCellStyle}>
                                            {actualDisplay}
                                        </td>
                                        <td style={{ ...valueCellStyle, fontWeight: 600, color: scoreColor, backgroundColor: scoreBg }}>
                                            {row.earned}
                                        </td>
                                    </tr>
                                );
                            })}
                        </React.Fragment>
                    ))}
                    <tr>
                        <td style={{ ...labelCellStyle, backgroundColor: '#e5e7eb' }}>Total</td>
                        <td style={{ ...valueCellStyle, backgroundColor: '#e5e7eb' }}></td>
                        <td style={{ ...valueCellStyle, backgroundColor: '#e5e7eb', fontWeight: 700 }}>{grandTotal}</td>
                        <td style={{ ...valueCellStyle, backgroundColor: '#e5e7eb' }}></td>
                        <td style={{ ...valueCellStyle, backgroundColor: '#e5e7eb' }}></td>
                        <td style={{ ...valueCellStyle, backgroundColor: '#e5e7eb' }}></td>
                        <td style={{ ...valueCellStyle, backgroundColor: '#e5e7eb', fontWeight: 700 }}>{grandEarned}</td>
                    </tr>
                    <tr>
                        <td style={labelCellStyle}>Minimum pass rate</td>
                        <td style={valueCellStyle}></td>
                        <td style={{ ...valueCellStyle, fontWeight: 600 }}>{PASS_THRESHOLD}</td>
                        <td style={valueCellStyle}></td>
                        <td style={valueCellStyle}></td>
                        <td style={valueCellStyle}></td>
                        <td style={valueCellStyle}></td>
                    </tr>
                    <tr>
                        <td style={{ ...labelCellStyle, fontSize: '13px' }}>Result</td>
                        <td style={valueCellStyle}></td>
                        <td style={valueCellStyle}></td>
                        <td style={valueCellStyle}></td>
                        <td style={valueCellStyle}></td>
                        <td style={valueCellStyle}></td>
                        <td style={{
                            ...valueCellStyle,
                            fontWeight: 700,
                            fontSize: '13px',
                            color: passed ? '#16a34a' : '#dc2626',
                            backgroundColor: passed ? '#dcfce7' : '#fee2e2',
                        }}>
                            {passed ? 'PASS' : 'FAIL'}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default DublinScorecardTable;
